import React, { useEffect, useReducer, useRef, useState } from "react";
import "./ChatInput.css";
import Send from "../assets/Send.svg";
import KshemaLogo from "../assets/KshemaLogo.svg";
import axios from "axios";
import {
  authenticateAfterOTPValidation,
  generateMessage,
  generateMessageStream,
  getAllData,
  getCodeId,
  getOTP,
  verifyOTP,
} from "../services/apiConstant";
import { generatePKCEParams, getFromLocalStorage } from "../utils/Util";
import {
  API_STATUS,
  ROLE,
  STATUS_MESSAGE,
  STORAGE_KEY,
} from "../utils/Constant";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ArrowUp from "../assets/ArrowUp.svg";
import ArrowDown from "../assets/ArrowDown.svg";
import Microphone from "../assets/Microphone.svg";
import { v4 as uuidv4 } from "uuid";
import { refreshAuthToken } from "../utils/AuthUtils";
import { Streamdown } from "streamdown";
// Required by Streamdown's own animate plugin (the fadeIn word-reveal effect
// `animated` turns on below) — without this, it renders the `data-sd-animate`
// markup but no CSS ever turns it into a visible animation.
import "streamdown/styles.css";
import {
  VoiceState,
  voiceReducer,
  initialVoiceState,
} from "../voice/voiceMachine";
import { ProgressiveChunker, TextChunk } from "../voice/chunker";
import { VoiceActivityDetector } from "../voice/vad";
import { BargeInDetector } from "../voice/bargeIn";
import { stt, tts, prewarm } from "../voice/voiceClient";
import { PlaybackEngine, HighlightRange } from "../voice/playback";
import { sarvamToAppLanguage, appToSarvamLanguage } from "../voice/languageMap";
import {
  SILENCE_END_MS,
  MIN_SPEECH_MS,
  MAX_UTTERANCE_MS,
  RECORDER_RESET_MS,
  SESSION_IDLE_MS,
  AUDIO_BITRATE_BPS,
  FIRST_CHUNK_MAX_CHARS,
  CHUNK_MIN_CHARS,
  CHUNK_HARD_MAX_CHARS,
  LANG_CONFIDENCE_MIN,
  SENTENCE_BOUNDARY,
  BARGE_IN_ENABLED,
  ECHO_CALIBRATION_MS,
  BARGE_IN_MARGIN,
  BARGE_IN_CONFIRM_MS,
  BARGE_IN_MIN_PLAYBACK_MS,
  BARGE_IN_MAX_PER_MIN,
} from "../voice/constants";
interface ReturnMessage {
  content: string;
  forLogin?: boolean;
}
interface ChatInputProps {
  message: string;
  setMessage: (newMessage: string) => void;
}
interface Message {
  role: string;
  content: string;
  forLogin?: boolean | null;
  feedback?: "like" | "dislike" | null;
  isStreaming?: boolean;
  hasStarted?: boolean;
}
interface RequestMessage {
  role: string;
  content: string;
}
const LOGIN_STAGE = {
  MOBILE_NUMBER: "mobile_number",
  OTP_VERIFICATION: "otp_verification",
  OTP_VERIFICATION_IN_PROGRESS: "otp_verification_progress",
  OTP_VERIFICATION_ERROR: "otp_verification_error",
  OTP_VERIFIED: "otp_verified",
};
const ChatInput: React.FC<ChatInputProps> = ({ message, setMessage }) => {
  const [chatHistory, setChatHistory] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState("");
  const [userName, setUserName] = useState<string | null>(null);
  const [otpReferenceId, setOtpReferenceId] = useState<string | null>(null);
  const [codeId, setCodeId] = useState<string | null>(null);
  const [visibleCommentIndex, setVisibleCommentIndex] = useState<number | null>(
    null
  );
  const commentBoxRef = useRef<HTMLDivElement>(null);
  const [isCopied, setIsCopied] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [uuid, setUuid] = useState<string | null>(null);
  const [feedbackId, setFeedbackId] = useState<string | null>(null);
  const [selectLanguage, setSelectLanguageLocal] = useState<string>("eng_Latn");
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  // --- Conversational voice mode -------------------------------------
  // See src/voice/ for the state machine, chunker, VAD, TTS/STT client and
  // playback engine. Everything below is the imperative glue: acquiring the
  // mic once per conversation, driving VAD/recorder lifecycle, and bridging
  // into the EXISTING chat send/stream path (handleNormalChat / buildRequestParam
  // below gained an optional override-text/override-language/isVoiceTurn
  // parameter for this — the text-chat call sites are unaffected).
  const [voiceMachine, dispatchVoice] = useReducer(voiceReducer, initialVoiceState);
  const voiceStateRef = useRef<VoiceState>(voiceMachine.state);
  const turnIdRef = useRef<number>(voiceMachine.turnId);
  useEffect(() => {
    voiceStateRef.current = voiceMachine.state;
    turnIdRef.current = voiceMachine.turnId;
  }, [voiceMachine]);

  const micStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const vadRef = useRef<VoiceActivityDetector | null>(null);
  const voiceRecorderRef = useRef<MediaRecorder | null>(null);
  const voiceChunksRef = useRef<Blob[]>([]);
  const speechStartAtRef = useRef<number | null>(null);
  const pendingTurnOutcomeRef = useRef<"proceed" | "discard">("proceed");
  const recorderResetTimerRef = useRef<number | null>(null);
  const maxUtteranceTimerRef = useRef<number | null>(null);
  const sessionIdleTimerRef = useRef<number | null>(null);
  const progressiveChunkerRef = useRef<ProgressiveChunker | null>(null);
  const playbackEngineRef = useRef<PlaybackEngine | null>(null);
  const isVoiceTurnRef = useRef<boolean>(false);
  const firstVoiceChunkSentRef = useRef<boolean>(false);
  const activeTurnLanguageRef = useRef<string>("eng_Latn");
  const activeAssistantIndexRef = useRef<number>(-1);
  // Barge-in (Segment V1.3)
  const bargeInDetectorRef = useRef<BargeInDetector | null>(null);
  const bargeInTimestampsRef = useRef<number[]>([]); // rolling window, for the per-minute circuit breaker
  const bargeInConsecutiveEmptyRef = useRef<number>(0);
  const bargeInSessionDisabledRef = useRef<boolean>(false);
  const turnOriginRef = useRef<"normal" | "bargeIn">("normal");
  const [inputLevel, setInputLevel] = useState(0);
  const [highlightRange, setHighlightRange] = useState<HighlightRange | null>(null);
  const [micPermissionDenied, setMicPermissionDenied] = useState(false);
  const [verifierAndChallenge, setVerifierAndChallenge] = useState<any>();
  const [selectLanguageName, setSelectLanguageName] =
    useState<string>("English");
  const [queryNum, setQueryNum] = useState<number>(1);
  const [traceIds, setTraceIds] = useState<string[]>([]);
  const [loginStage, setLoginState] = useState<string | null>(null);
  const languageOptions = [
    { name: "English", code: "eng_Latn" },
    { name: "Telugu", code: "tel_Telu" },
    { name: "Hindi", code: "hin_Deva" },
    { name: "Tamil", code: "tam_Taml" },
  ];
  const token = "some-string";
  const authData: any = JSON.parse(getFromLocalStorage(STORAGE_KEY.AUTH_DATA));
  const email = authData?.email;

  useEffect(() => {
    const refreshToken = async () => {
      await refreshAuthToken(authData?.resetToken);
    };
    refreshToken();
  }, []);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const { uuid } = await getAllData(token, email);
        setUuid(uuid);
      } catch (error) {
        console.error("Failed to fetch data.", error);
        // Local-dev fallback: the backend's session DB may be unreachable
        // (e.g. kgpt-backend running outside the cluster) — generate a
        // client-side session id so chat/voice can still be exercised
        // end-to-end. This session is not persisted server-side; that's
        // expected here and not something to rely on outside local testing.
        setUuid(uuidv4());
      }
    };
    fetchData();
  }, []);
  //Log pre and post responses logs
  const logMessage = async (
    sessionId: string,
    messageType: "display" | "process",
    role: "user" | "assistant" | "feedback",
    message: string
  ) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/chat/log_session`,
        {
          session_id: sessionId,
          message_type: messageType,
          message,
          role,
        }
      );
    } catch (error) {
      console.error(`Failed to log ${messageType} message`, error);
    }
  };
  const isValidMobileNumber = (mobileNumber: string) => {
    const regex = /^\d{10}$/;
    return regex.test(mobileNumber);
  };
  const sendMessage = async () => {
    if (!message.trim()) return;
    setQueryNum(queryNum + 1);
    const text = message;
    setMessage("");

    if (!loginStage || loginStage === LOGIN_STAGE.OTP_VERIFIED) {
      addToChatHistory(ROLE.USER, { content: text, forLogin: false });
      await handleNormalChat(text);
    } else {
      addToChatHistory(ROLE.USER, { content: text, forLogin: true });
      await handleLoginFlow(null, null);
    }
  };
  // Voice turns bypass `message`/`setMessage` entirely (that state is only
  // ever read/written synchronously here, so passing the transcript through
  // explicitly avoids relying on a setState that hasn't committed yet).
  const sendVoiceMessage = async (text: string, language: string) => {
    if (!text.trim()) return;
    setQueryNum((prev) => prev + 1);
    addToChatHistory(ROLE.USER, { content: text, forLogin: false });
    await handleNormalChat(text, true, language);
  };
  const addToChatHistory = (role: string, data: ReturnMessage) => {
    setChatHistory((prev) => [
      ...prev,
      {
        role,
        content: data?.content,
        forLogin: data?.forLogin,
      },
    ]);
  };
  const buildRequestParam = (overrideQuery?: string, overrideLanguage?: string) => {
    const history: RequestMessage[] = [];
    chatHistory?.forEach((item) => {
      if (!item?.forLogin) {
        history.push({
          role: item?.role,
          content: item?.content,
        });
      }
    });
    return {
      messages: history,
      query: overrideQuery ?? message,
      session_id: uuid,
      span_id: "",
      target_language: overrideLanguage ?? selectLanguage,
      trace_id: "",
      email_id: email,
    };
  };
 const handleNormalChat = async (
    overrideQuery?: string,
    isVoiceTurn: boolean = false,
    overrideLanguage?: string
  ) => {
    setLoading(true);
    setLoginState(null);
    isVoiceTurnRef.current = isVoiceTurn;
    // Captured once, read inside the callbacks below instead of the mutable
    // refs — a barge-in mid-stream bumps turnId to supersede this exact
    // call's chat response without resetting isVoiceTurnRef (the session is
    // still voice-active, just on a new turn), so isVoiceTurnRef.current
    // alone can't tell a superseded turn's late callbacks apart from the
    // new turn's. turnId can.
    const thisTurnId = turnIdRef.current;

    if (isVoiceTurn) {
      const lang = overrideLanguage ?? selectLanguage;
      activeTurnLanguageRef.current = lang;
      firstVoiceChunkSentRef.current = false;
      playbackEngineRef.current = createPlaybackEngine();
      if (!playbackEngineRef.current) {
        // No audio session to speak into — degrade to a text-only answer
        // for this turn rather than failing the whole request. The user
        // still gets their answer, just not read aloud.
        isVoiceTurn = false;
        isVoiceTurnRef.current = false;
      } else {
        progressiveChunkerRef.current = new ProgressiveChunker({
          firstChunkMaxChars: FIRST_CHUNK_MAX_CHARS,
          chunkMinChars: CHUNK_MIN_CHARS,
          chunkHardMaxChars: CHUNK_HARD_MAX_CHARS,
          sentenceBoundary: SENTENCE_BOUNDARY,
        });
      }
    }

    let assistantIndex = -1;

    // ✅ STEP 1: create EMPTY assistant message (stream placeholder)
    setChatHistory((prev) => {
      assistantIndex = prev.length;
      return [
        ...prev,
        {
          role: ROLE.ASSISTANT,
          content: loadingMessages[selectLanguage] || "Generating...",
          forLogin: false,
          isStreaming: true,
          hasStarted: false,
        },
      ];
    });
    activeAssistantIndexRef.current = assistantIndex;

    // ✅ STEP 2: start streaming
    await generateMessageStream(
      buildRequestParam(overrideQuery, overrideLanguage),

      // ✅ onToken → runs MANY times
      (token: string) => {



  setChatHistory((prev) => {
    const updated = [...prev];
    const msg = updated[assistantIndex];

    const newContent = msg.hasStarted
      ? msg.content + token
      : token;

    {/*console.log(
      "CONTENT_AFTER_APPEND =",
      JSON.stringify(newContent)
    );*/}

    updated[assistantIndex] = {
      ...msg,
      content: newContent,
      hasStarted: true,
    };

    return updated;
  });

  // Voice turn: feed the same tokens into the chunker and hand any newly
  // complete chunks to the playback engine, exactly as they arrive — this
  // is what gets the FIRST chunk speaking before the whole answer is done.
  // turnId guard: a barge-in may have superseded this exact call with a new
  // turn since it started (see thisTurnId's comment above) — a superseded
  // turn's late tokens must not reach the NEW turn's chunker/playback.
  if (isVoiceTurn && turnIdRef.current === thisTurnId && progressiveChunkerRef.current && playbackEngineRef.current) {
    const newChunks = progressiveChunkerRef.current.push(token);
    for (const c of newChunks) {
      playbackEngineRef.current.push(c);
      if (!firstVoiceChunkSentRef.current) {
        firstVoiceChunkSentRef.current = true;
        dispatchVoice({ type: "RESPONSE_READY" });
      }
    }
  }
},


     () => {
  setChatHistory((prev) => {
    const updated = [...prev];
    updated[assistantIndex] = {
      ...updated[assistantIndex],
      isStreaming: false,
    };
    return updated;
  });

  setLoading(false);

  if (isVoiceTurn && turnIdRef.current === thisTurnId && progressiveChunkerRef.current && playbackEngineRef.current) {
    const finalChunks = progressiveChunkerRef.current.flush();
    for (const c of finalChunks) {
      playbackEngineRef.current.push(c);
      if (!firstVoiceChunkSentRef.current) {
        firstVoiceChunkSentRef.current = true;
        dispatchVoice({ type: "RESPONSE_READY" });
      }
    }
    playbackEngineRef.current.end();
    // Nothing was ever speakable (empty response) -- no audio is coming, so
    // there's no SPEAKING state to wait for; go straight back to listening.
    if (!firstVoiceChunkSentRef.current) {
      dispatchVoice({ type: "ABORT_TURN" });
      startListeningCycle();
    }
  }
},

      (error: any) => {
        setLoading(false);

        if (isVoiceTurn && turnIdRef.current === thisTurnId) {
          // ABORT_TURN (not a THINKING/SPEAKING-specific event) because a
          // stream error can land here after some chunks already reached
          // SPEAKING — it recovers to LISTENING from either.
          playbackEngineRef.current?.stop();
          dispatchVoice({ type: "ABORT_TURN" });
          startListeningCycle();
        }

        const errorMsg =
          error?.error ||
          error?.message ||
          "";

        setChatHistory((prev) => {
          const updated = [...prev];

          for (let i = updated.length - 1; i >= 0; i--) {
            if (updated[i].role === ROLE.ASSISTANT && updated[i].isStreaming) {
              // OTP required case
              if (errorMsg.includes("otp_required")) {
                updated[i] = {
                  role: ROLE.ASSISTANT,
                  content: STATUS_MESSAGE.LOGIN_REQUIRED,
                  forLogin: true,
                  isStreaming: false,
                  hasStarted: true,
                };
              } 
              else {
                updated[i] = {
                  role: ROLE.ASSISTANT,
                  content: STATUS_MESSAGE.SOMETHING_WRONG,
                  forLogin: false,
                  isStreaming: false,
                  hasStarted: true,
                };
              }
              break;
            }
        }

        return updated;
      });

      // ✅ Trigger OTP flow only for OTP case
      if (errorMsg.includes("otp_required")) {
        setLoginState(LOGIN_STAGE.MOBILE_NUMBER);
        return;
      }

      // ✅ Generic error toast
      toast.error(STATUS_MESSAGE.SOMETHING_WRONG);
    },


    );
  }
  const getBotResponse = (response: any) => {
    if (
      response?.response?.data?.otp_validated === API_STATUS.OTP_NOT_VERIFIED
    ) {
      setLoginState(LOGIN_STAGE.MOBILE_NUMBER);
      return { content: STATUS_MESSAGE.LOGIN_REQUIRED, forLogin: true };
    }
    if (response?.data?.status === API_STATUS.OPERATION_SUCCESSFUL) {
      return {
        content: response.data?.response || STATUS_MESSAGE.NO_RESPONSE,
        forLogin: false,
      };
    }
    return {
      content: response?.data?.response || STATUS_MESSAGE.ERROR_FETCHING,
      forLogin: false,
    };
  };
  const handleLoginFlow = async (trace: any, span: any) => {
    let returnMessage: any;
    if (loginStage === LOGIN_STAGE.MOBILE_NUMBER) {
      returnMessage = await processMobileNumber(message);
      if (returnMessage?.content === STATUS_MESSAGE.RETRY_THIS) {
        returnMessage = await processMobileNumber(returnMessage?.message);
      }
    } else if (loginStage === LOGIN_STAGE.OTP_VERIFICATION) {
      returnMessage = await processOTPVerification(message);
    }
    finalizeResponse(trace, span, returnMessage);
  };
  const processMobileNumber = async (mobile: string) => {
    if (!isValidMobileNumber(mobile))
      return { content: STATUS_MESSAGE.ENTER_VALID_MOBILE, forLogin: true };
    setLoginState(LOGIN_STAGE.OTP_VERIFICATION);
    setLoading(true);
    setUserName(mobile);
    const otpResponse = await getOTP({
      recipient: mobile,
      channelType: "SMS",
      language: "en_us",
      placeholders: { user: "user" },
    });
    if (
      otpResponse?.status === API_STATUS.FAILED &&
      otpResponse?.errors?.length > 0 &&
      otpResponse?.errors[0]?.message?.includes("Jwt expired")
    ) {
      await refreshAuthToken(authData?.resetToken);
      return {
        content: STATUS_MESSAGE.RETRY_THIS,
        forLogin: true,
        message: mobile,
      };
    }
    setLoading(false);

    if (otpResponse?.status === 200 && otpResponse?.message === "OTP Sent") {
      setOtpReferenceId(otpResponse?.data?.referenceId);
      const { verifier, challenge } = generatePKCEParams();
      setVerifierAndChallenge({ verifier, challenge });
      const codeIdResponse = await getCodeId({
        clientId: "Kshema2.0",
        codeChallenge: challenge,
        codeChallengeMethod: "S256",
      });
      setCodeId(codeIdResponse?.data[0]?.codeId);
      return { content: STATUS_MESSAGE.ENTER_OTP, forLogin: true };
    }
    setOtpReferenceId(null);
    setCodeId(null);
    return { content: STATUS_MESSAGE.SOMETHING_WRONG, forLogin: true };
  };
  const processOTPVerification = async (otp: string) => {
    setLoginState(LOGIN_STAGE.OTP_VERIFICATION_IN_PROGRESS);
    setLoading(true);
    const verifyResponse = await verifyOTP({
      codeId,
      codeVerifier: verifierAndChallenge?.verifier,
      codeExchangeType: "BOTH",
      username: userName,
      otp,
      otpIdentifier: otpReferenceId,
    });
    setLoading(false);
    if (verifyResponse?.status === API_STATUS.STATUS_SUCCESS) {
      setLoading(true);
      setLoginState(LOGIN_STAGE.OTP_VERIFIED);
      const response = await authenticateAfterOTPValidation({
        additional_data: verifyResponse?.data[0],
        mobile_number: userName,
        session_id: uuid,
        span_id: "",
        trace_id: "",
      });
      setLoading(false);
      return {
        content:
          response?.status === API_STATUS.OPERATION_SUCCESSFUL
            ? STATUS_MESSAGE.OPT_VERIFIED
            : STATUS_MESSAGE.OPT_VERIFICATION_ERROR,
        forLogin: true,
      };
    }
    setLoginState(LOGIN_STAGE.MOBILE_NUMBER);
    return { content: STATUS_MESSAGE.OPT_VERIFICATION_ERROR, forLogin: true };
  };
  const finalizeResponse = (trace: any, span: any, data: ReturnMessage) => {
    addToChatHistory(ROLE.ASSISTANT, data);
  };
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory]);
  const sendFeedback = async (rating: number, index: number) => {
    let userMessage, botResponse;
    for (let i = index; i >= 0; i--) {
      if (chatHistory[i].role === "user") {
        userMessage = chatHistory[i].content;
        botResponse = chatHistory[i + 1]?.content;
        break;
      }
    }
    if (!userMessage || !botResponse) {
      console.error(
        "Missing user message or assistant response. Cannot send feedback."
      );
      return;
    }
    const query = userMessage;
    const response = botResponse;

    try {
      const apiResponse = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/store_rating`,
        {
          display_messages: [
            { content: userMessage, role: ROLE.USER },
            { content: botResponse, role: "assistant" },
          ],
          process_messages: [
            { content: userMessage, role: ROLE.USER },
            { content: botResponse, role: "assistant" },
          ],
          query: query,
          rating: rating,
          response: response,
          session_id: uuid,
          email_id: email,
          trace_id: "",
          span_id: "",
        }
      );
      if (apiResponse.data && apiResponse.data.feedback_id) {
        setFeedbackId(apiResponse.data.feedback_id);
        toast.success("Feedback saved successfully!");
      }
    } catch (error) {
      console.error("Failed to send feedback:", error);
    }
  };
  const handleThumbsUp = (index: number) => {
    if (chatHistory[index].feedback === "like") {
      console.log("Already liked.");
      return;
    }
    setChatHistory((prev) =>
      prev.map((msg, i) => (i === index ? { ...msg, feedback: "like" } : msg))
    );
    sendFeedback(1, index);
    setVisibleCommentIndex(null);
  };
  const handleThumbsDown = (index: number) => {
    if (chatHistory[index].feedback === "dislike") {
      console.log("Already disliked.");
      return;
    }
    setChatHistory((prev) =>
      prev.map((msg, i) =>
        i === index ? { ...msg, feedback: "dislike" } : msg
      )
    );
    sendFeedback(0, index);
    setVisibleCommentIndex(index);
  };
  const handleSaveComment = async () => {
    if (comment.trim()) {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/update_comment`,
          {
            feedback_id: feedbackId,
            comment: comment,
            trace_id: "",
            span_id: "",
          }
        );
        toast.success("Comment saved successfully!");
      } catch (error) {
        console.error("Failed to save comment:", error);
        toast.error("Failed to save comment");
      }
    }
    setComment("");
    setVisibleCommentIndex(null);
  };
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        commentBoxRef.current &&
        !commentBoxRef.current.contains(event.target as Node)
      ) {
        setVisibleCommentIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const handleCopy = (msgText: string) => {
    navigator.clipboard
      .writeText(msgText)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2500);
      })
      .catch((err) => console.error("Failed to copy text: ", err));
  };
  const loadingMessages: { [key: string]: string } = {
    eng_Latn: "Generating...",
    tel_Telu: "సృష్టిస్తున్నాను...", // Telugu
    hin_Deva: "प्रक्रिया जारी है...", // Hindi
    tam_Taml: "உருவாக்கப்படுகிறது...", // Tamil
  };
  const handleLanguageSelect = (langCode: string, name: string) => {
    setSelectLanguageLocal(langCode);
    setSelectLanguageName(name);
    setIsDropdownOpen(false);
  };
  const handleToggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };
  // --- Conversational voice mode: orchestration -----------------------
  // (state/refs declared near the top of the component; see the comment
  // there for an overview of src/voice/.)

  const clearVoiceTimers = () => {
    if (recorderResetTimerRef.current !== null) {
      window.clearTimeout(recorderResetTimerRef.current);
      recorderResetTimerRef.current = null;
    }
    if (maxUtteranceTimerRef.current !== null) {
      window.clearTimeout(maxUtteranceTimerRef.current);
      maxUtteranceTimerRef.current = null;
    }
    if (sessionIdleTimerRef.current !== null) {
      window.clearTimeout(sessionIdleTimerRef.current);
      sessionIdleTimerRef.current = null;
    }
  };

  const resetSessionIdleTimer = () => {
    if (sessionIdleTimerRef.current !== null) {
      window.clearTimeout(sessionIdleTimerRef.current);
    }
    sessionIdleTimerRef.current = window.setTimeout(() => {
      dispatchVoice({ type: "SESSION_TIMEOUT" });
    }, SESSION_IDLE_MS);
  };

  const createPlaybackEngine = (): PlaybackEngine | null => {
    const audioContext = audioContextRef.current;
    if (!audioContext) {
      // The audio session went away between turns (session torn down, or a
      // dev-mode remount) — this must not be an uncaught crash. Callers
      // check for null and abort the voice turn instead of speaking it.
      console.error("Voice: no AudioContext available, cannot create playback engine");
      return null;
    }
    return new PlaybackEngine(
      audioContext,
      async (chunk: TextChunk, signal: AbortSignal) => {
        const sarvamLang = appToSarvamLanguage(activeTurnLanguageRef.current);
        return tts(chunk.text, sarvamLang, { signal });
      },
      {
        onHighlight: (range) => setHighlightRange(range),
        onChunkFailed: (chunk, err) => {
          // One chunk failing to speak must not kill the rest of the
          // answer's playback (or the conversation) — just note it.
          console.warn("Voice: TTS chunk failed, skipping it", chunk, err);
        },
        onDone: () => {
          dispatchVoice({ type: "PLAYBACK_DONE" });
        },
      }
    );
  };

  /** Starts (or restarts) one listening cycle: fresh recorder, VAD
   * (re-)calibration, RECORDER_RESET_MS + SESSION_IDLE_MS timers. Assumes
   * micStreamRef/audioContextRef/vadRef are already set up. */
  const startListeningCycle = () => {
    const stream = micStreamRef.current;
    if (!stream) return;

    voiceChunksRef.current = [];
    const recorder = new MediaRecorder(stream, { audioBitsPerSecond: AUDIO_BITRATE_BPS });
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) voiceChunksRef.current.push(event.data);
    };
    recorder.onstop = () => handleRecorderStopped();
    voiceRecorderRef.current = recorder;
    recorder.start();

    speechStartAtRef.current = null;
    vadRef.current?.stop();
    vadRef.current?.start();

    if (recorderResetTimerRef.current !== null) window.clearTimeout(recorderResetTimerRef.current);
    recorderResetTimerRef.current = window.setTimeout(() => {
      // No speech yet — restart with a fresh (small) recorder rather than
      // letting the blob grow unboundedly while nothing is being said.
      if (voiceStateRef.current === "LISTENING") {
        voiceRecorderRef.current?.stop();
        startListeningCycle();
      }
    }, RECORDER_RESET_MS);

    resetSessionIdleTimer();
  };

  const handleVadLevel = (rms: number) => {
    setInputLevel(rms);
  };

  const handleSpeechStart = () => {
    if (voiceStateRef.current !== "LISTENING") return;
    if (recorderResetTimerRef.current !== null) {
      window.clearTimeout(recorderResetTimerRef.current);
      recorderResetTimerRef.current = null;
    }
    turnOriginRef.current = "normal";
    speechStartAtRef.current = performance.now();
    dispatchVoice({ type: "SPEECH_DETECTED" });
    resetSessionIdleTimer();

    maxUtteranceTimerRef.current = window.setTimeout(() => {
      finishUtterance();
    }, MAX_UTTERANCE_MS);
  };

  /** Fires when BargeInDetector confirms the user is speaking over playback.
   * Stops playback immediately, discards it (no salvage — it contains
   * echo), and jumps straight into a fresh CAPTURING turn. */
  const handleBargeInTrigger = () => {
    if (voiceStateRef.current !== "SPEAKING") return; // stale trigger from a torn-down detector

    const now = Date.now();
    bargeInTimestampsRef.current = bargeInTimestampsRef.current.filter((t) => now - t < 60000);
    bargeInTimestampsRef.current.push(now);
    if (bargeInTimestampsRef.current.length > BARGE_IN_MAX_PER_MIN && !bargeInSessionDisabledRef.current) {
      bargeInSessionDisabledRef.current = true;
      console.warn(
        `Voice: barge-in disabled for this session — exceeded ${BARGE_IN_MAX_PER_MIN}/min (circuit breaker)`
      );
    }

    playbackEngineRef.current?.stop(); // sources stopped, TTS fetches aborted, highlight cleared
    bargeInDetectorRef.current?.disconnect();
    bargeInDetectorRef.current = null;

    turnOriginRef.current = "bargeIn";
    speechStartAtRef.current = performance.now(); // speech already confirmed by BargeInDetector itself

    voiceChunksRef.current = [];
    const stream = micStreamRef.current;
    if (stream) {
      const recorder = new MediaRecorder(stream, { audioBitsPerSecond: AUDIO_BITRATE_BPS });
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) voiceChunksRef.current.push(event.data);
      };
      recorder.onstop = () => handleRecorderStopped();
      voiceRecorderRef.current = recorder;
      recorder.start();
    }
    // Reuse the LISTENING VAD's last-calibrated threshold to detect THIS
    // utterance's end — do not recalibrate now, live speech would corrupt
    // the ambient reading (see vad.ts's resumeAsSpeaking doc comment).
    vadRef.current?.resumeAsSpeaking();

    if (maxUtteranceTimerRef.current !== null) window.clearTimeout(maxUtteranceTimerRef.current);
    maxUtteranceTimerRef.current = window.setTimeout(() => {
      finishUtterance();
    }, MAX_UTTERANCE_MS);
    resetSessionIdleTimer();

    dispatchVoice({ type: "BARGE_IN_DETECTED" });
  };

  const finishUtterance = () => {
    if (maxUtteranceTimerRef.current !== null) {
      window.clearTimeout(maxUtteranceTimerRef.current);
      maxUtteranceTimerRef.current = null;
    }
    vadRef.current?.stop();
    const utteranceMs = speechStartAtRef.current !== null ? performance.now() - speechStartAtRef.current : 0;
    pendingTurnOutcomeRef.current = utteranceMs >= MIN_SPEECH_MS ? "proceed" : "discard";
    dispatchVoice({ type: pendingTurnOutcomeRef.current === "proceed" ? "TURN_ENDED" : "TURN_DISCARDED" });
    voiceRecorderRef.current?.stop(); // -> handleRecorderStopped()
  };

  const handleRecorderStopped = () => {
    const outcome = pendingTurnOutcomeRef.current;
    const capturedTurnId = turnIdRef.current;
    const blob = new Blob(voiceChunksRef.current, {
      type: (voiceRecorderRef.current?.mimeType || "audio/webm").split(";")[0],
    });
    voiceChunksRef.current = [];

    if (outcome === "discard") {
      if (turnIdRef.current === capturedTurnId) {
        startListeningCycle();
      }
      return;
    }
    void runTranscriptionAndRespond(blob, capturedTurnId);
  };

  const runTranscriptionAndRespond = async (blob: Blob, capturedTurnId: number) => {
    const abortController = new AbortController();
    try {
      // Fire prewarm() in parallel with stt() — a trivial TTS call that
      // warms the connection, worth ~500ms on first audio (connections go
      // cold after ~15s idle).
      prewarm(appToSarvamLanguage(selectLanguage));
      const result = await stt(blob, abortController.signal);
      if (turnIdRef.current !== capturedTurnId) return; // mic was pressed mid-flight — this turn is cancelled

      const transcript = result.transcript.trim();
      if (!transcript) {
        // Two consecutive empty transcripts from barge-in specifically is
        // the signature of the app hearing itself — a normal (non-barge-in)
        // empty turn doesn't count toward or reset this.
        if (turnOriginRef.current === "bargeIn") {
          bargeInConsecutiveEmptyRef.current += 1;
          if (bargeInConsecutiveEmptyRef.current >= 2 && !bargeInSessionDisabledRef.current) {
            bargeInSessionDisabledRef.current = true;
            console.warn(
              "Voice: barge-in disabled for this session — two consecutive empty transcripts from barge-in (likely self-hearing)"
            );
          }
        }
        dispatchVoice({ type: "TRANSCRIPT_EMPTY" });
        startListeningCycle();
        return;
      }
      if (turnOriginRef.current === "bargeIn") {
        bargeInConsecutiveEmptyRef.current = 0;
      }

      const detectedAppLang =
        result.probability >= LANG_CONFIDENCE_MIN ? sarvamToAppLanguage(result.language) : null;
      const effectiveLanguage = detectedAppLang || selectLanguage;
      if (detectedAppLang && detectedAppLang !== selectLanguage) {
        const opt = languageOptions.find((o) => o.code === detectedAppLang);
        setSelectLanguageLocal(detectedAppLang);
        if (opt) setSelectLanguageName(opt.name);
      }

      dispatchVoice({ type: "TRANSCRIPT_READY" });
      await sendVoiceMessage(transcript, effectiveLanguage);
    } catch (err) {
      if (turnIdRef.current !== capturedTurnId) return;
      console.error("Voice: transcription failed", err);
      // Still in TRANSCRIBING at this point (we never reached
      // TRANSCRIPT_READY) — TRANSCRIPT_EMPTY is the valid transition out of
      // it back to LISTENING; RESPONSE_FAILED only applies from THINKING.
      dispatchVoice({ type: "TRANSCRIPT_EMPTY" });
      startListeningCycle();
    }
  };

  const teardownVoiceSession = () => {
    clearVoiceTimers();
    vadRef.current?.disconnect();
    vadRef.current = null;
    bargeInDetectorRef.current?.disconnect();
    bargeInDetectorRef.current = null;
    try {
      voiceRecorderRef.current?.stop();
    } catch {
      /* already stopped */
    }
    voiceRecorderRef.current = null;
    voiceChunksRef.current = [];
    playbackEngineRef.current?.stop();
    playbackEngineRef.current = null;
    progressiveChunkerRef.current = null;
    isVoiceTurnRef.current = false;
    setHighlightRange(null);
    setInputLevel(0);
    micStreamRef.current?.getTracks().forEach((t) => t.stop());
    micStreamRef.current = null;
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      void audioContextRef.current.close();
    }
    audioContextRef.current = null;
  };

  const handleMicPress = async () => {
    if (voiceStateRef.current !== "IDLE") {
      // Any state -> pressing the mic cancels everything and returns to
      // IDLE; the effect below (curr === "IDLE") does the actual teardown.
      dispatchVoice({ type: "MIC_PRESSED" });
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;
      // Created on this user gesture, per the Web Audio API's autoplay rules.
      const AudioContextCtor: typeof AudioContext =
        window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContextCtor();
      audioContextRef.current = audioContext;
      vadRef.current = new VoiceActivityDetector(
        audioContext,
        stream,
        { silenceEndMs: SILENCE_END_MS },
        { onLevel: handleVadLevel, onSpeechStart: handleSpeechStart, onTurnEnd: finishUtterance }
      );
      setMicPermissionDenied(false);
      dispatchVoice({ type: "MIC_PRESSED" });
      startListeningCycle();
    } catch (error) {
      console.error("Failed to start conversational voice mode:", error);
      setMicPermissionDenied(true);
    }
  };

  // SPEAKING: the recorder/VAD are already idle by the time we get here
  // (CAPTURING stopped them via finishUtterance() before TRANSCRIBING even
  // started) — capture doesn't need pausing again, it was never resumed.
  // This is what "prevents the mic hearing the TTS output": nothing is
  // listening during THINKING/SPEAKING at all, except the barge-in detector
  // below (Segment V1.3), which is a deliberately separate, much stricter
  // listener (see bargeIn.ts). LISTENING (looped back from SPEAKING/a
  // discarded/empty turn): start a fresh cycle.
  const prevVoiceStateRef = useRef<VoiceState>(voiceMachine.state);
  useEffect(() => {
    const prev = prevVoiceStateRef.current;
    const curr = voiceMachine.state;
    if (curr === "LISTENING" && prev === "SPEAKING") {
      startListeningCycle();
    }
    if (curr === "IDLE" && prev !== "IDLE") {
      teardownVoiceSession();
    }
    // Barge-in: recalibrate fresh on every entry to SPEAKING (a new
    // instance each time, per bargeIn.ts's doc comment); disconnect on
    // every exit, regardless of which event caused it.
    if (curr === "SPEAKING" && prev !== "SPEAKING") {
      if (BARGE_IN_ENABLED && !bargeInSessionDisabledRef.current && micStreamRef.current && audioContextRef.current) {
        bargeInDetectorRef.current = new BargeInDetector(
          audioContextRef.current,
          micStreamRef.current,
          {
            echoCalibrationMs: ECHO_CALIBRATION_MS,
            bargeInMargin: BARGE_IN_MARGIN,
            confirmMs: BARGE_IN_CONFIRM_MS,
            minArmMs: BARGE_IN_MIN_PLAYBACK_MS,
            floorThreshold: vadRef.current?.getThreshold() ?? 0,
          },
          { onTrigger: handleBargeInTrigger }
        );
        bargeInDetectorRef.current.start();
      }
    }
    if (prev === "SPEAKING" && curr !== "SPEAKING") {
      bargeInDetectorRef.current?.disconnect();
      bargeInDetectorRef.current = null;
    }
    prevVoiceStateRef.current = curr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceMachine.state]);

  // Release the mic/AudioContext if the component unmounts mid-conversation.
  useEffect(() => {
    return () => {
      teardownVoiceSession();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Barge-in armed: read live at render time, not cached — reflects the
  // circuit breaker (a ref, so no extra re-render is needed for this alone;
  // it's already re-evaluated whenever voiceMachine.state flips to SPEAKING).
  const bargeInArmed =
    BARGE_IN_ENABLED && voiceMachine.state === "SPEAKING" && !bargeInSessionDisabledRef.current;
  const voiceStateLabel: Record<VoiceState, string> = {
    IDLE: "",
    LISTENING: "Listening…",
    CAPTURING: "Listening…",
    TRANSCRIBING: "Transcribing…",
    THINKING: "Thinking…",
    SPEAKING: bargeInArmed ? "Speaking… (say something to interrupt)" : "Speaking…",
  };
  return (
    <div className="flex flex-col w-full max-w-5xl mx-auto mt-4 h-full ">
      <ToastContainer />
      <div
        ref={chatContainerRef}
        className="flex-1 px-4 custom-scrollbar overflow-y-auto"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {chatHistory.map((msg, index) => (
          <div key={index}>
            <div
              className={`flex ${
                msg.role === "user" ? "justify-end" : "justify-start"
              }`}
            >
              {msg.role === "assistant" && (
                <img
                  src={KshemaLogo}
                  alt="Bot Logo"
                  className="w-8 h-8 rounded-full mr-2"
                  style={{ marginTop: "-1px" }}
                />
              )}

              <div
                className={`p-3 rounded-[32px] mb-[20px] ${
                  msg.role === "user" ? "bg-blue-100" : "white"
                }`}
              >
                {msg.role === "assistant" && !msg.forLogin ? (
                  <div className="markdown-body">
                    {/*
                      Render the RAW markdown content directly.
                      Streamdown parses markdown (bullets, **bold**, ***bold-italic***)
                      and safely handles partial/incomplete tokens while streaming.
                      Do NOT pre-process the text with regex — that splits the
                      ** / *** markers across blocks and breaks bold intermittently.

                      Voice playback highlight: split into "spoken so far" /
                      "not yet spoken" at the current highlightEnd offset,
                      leaning on Streamdown's own partial-markdown tolerance
                      (already relied on above for streaming) rather than
                      re-parsing markdown ourselves — same node types/keys
                      either way, so this never re-mounts or moves scroll.
                    */}
                    {highlightRange && index === activeAssistantIndexRef.current ? (
                      <>
                        <span className="voice-highlight-spoken">
                          <Streamdown animated={false}>
                            {msg.content.slice(0, highlightRange.highlightEnd)}
                          </Streamdown>
                        </span>
                        <Streamdown animated caret="block">
                          {msg.content.slice(highlightRange.highlightEnd)}
                        </Streamdown>
                      </>
                    ) : (
                      <Streamdown animated caret="block">
                        {msg.content}
                      </Streamdown>
                    )}
                  </div>
                ) : (
                  msg.content
                )}
              </div>
            </div>
            {msg.role === "assistant" && !msg.isStreaming && (
              <div className="feedback-section">
                {/* Thumbs Up */}
                <div
                  className={`like ${msg.feedback === "like" ? "active" : ""}`}
                  onClick={() => {
                    if (msg.feedback !== "like" && msg.feedback !== "dislike") {
                      handleThumbsUp(index);
                    }
                  }}
                  style={{
                    cursor:
                      msg.feedback === "dislike" ? "not-allowed" : "pointer",
                  }}
                >
                  <i className="fa fa-thumbs-up text-xl" aria-hidden="true"></i>
                </div>
                {/* Thumbs Down */}
                <div
                  className={`dislike ${
                    msg.feedback === "dislike" ? "active" : ""
                  }`}
                  onClick={() => {
                    if (msg.feedback !== "dislike" && msg.feedback !== "like") {
                      handleThumbsDown(index);
                    }
                  }}
                  style={{
                    cursor:
                      msg.feedback === "dislike" ? "not-allowed" : "pointer",
                  }}
                >
                  <i
                    className="fa fa-thumbs-down text-xl"
                    aria-hidden="true"
                  ></i>
                </div>
                <div
                  className={`copy-text ${isCopied ? "active" : ""}`}
                  style={{ marginTop: "3px" }}
                >
                  <button onClick={() => handleCopy(msg.content)}>
                    <i className="fa fa-clone text-xl"></i>
                  </button>
                </div>
              </div>
            )}
            {visibleCommentIndex !== null && (
              <div className="modal-overlay">
                <div className="modal-content">
                  <textarea
                    placeholder="Enter your comment..."
                    value={comment}
                    className="comment-box"
                    onChange={(e) => setComment(e.target.value)}
                  ></textarea>
                  <button className="save-btn" onClick={handleSaveComment}>
                    Save
                  </button>
                  <button
                    className="cancel-btn"
                    onClick={() => setVisibleCommentIndex(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {micPermissionDenied && (
        <div className="flex items-center justify-center my-2">
          <span className="text-sm text-gray-500">
            Microphone access was denied — you can still type your questions below.
          </span>
        </div>
      )}
      <div className="language-sec">
        <div className="flex items-center gap-[10px]">
          <p className="profile-headings">Chat Language:</p>
          <div
            className="lang-display cursor-pointer"
            onClick={handleToggleDropdown}
          >
            {selectLanguageName}
            <img
              src={isDropdownOpen ? ArrowUp : ArrowDown}
              alt="Dropdown Icon"
              className="cursor-pointer arrow-click"
              onClick={handleToggleDropdown}
            />
          </div>
        </div>
        {isDropdownOpen && (
          <div className="language-dropdown">
            {languageOptions.map(({ name, code }) => (
              <div
                key={code}
                className="language-item"
                style={{
                  backgroundColor:
                    selectLanguage === code ? "#E4EDFE" : "transparent",
                }}
                onClick={() => handleLanguageSelect(code, name)}
              >
                {name}
              </div>
            ))}
          </div>
        )}
      </div>
      {voiceMachine.state !== "IDLE" ? (
        // 🎤 Conversational voice mode — one pill for every non-IDLE state,
        // labelled per voiceStateLabel. The X always cancels back to IDLE.
        <div
          className={`w-full bg-white shadow-md rounded-full flex items-center justify-between p-[19px] border border-gray-300 voice-state-${voiceMachine.state.toLowerCase()}`}
          style={{ boxShadow: "0px 4.27px 34.18px -4.27px #638cf352" }}
        >
          <button
            onClick={handleMicPress}
            title="Stop and return to typing"
            className="text-white bg-blue-900 w-6 h-6 rounded-full flex items-center justify-center"
          >
            <i className="fa fa-times text-sm" />
          </button>
          <div className="flex-1 mx-4 flex items-center space-x-1 overflow-hidden">
            {(voiceMachine.state === "LISTENING" || voiceMachine.state === "CAPTURING") ? (
              // Live input-level indicator — bar heights driven by the VAD's
              // RMS reading so the user can see it hears them.
              [...Array(20)].map((_, i) => {
                const jitter = 0.6 + 0.4 * Math.abs(Math.sin(i * 1.7));
                const height = 5 + Math.min(inputLevel * 400, 30) * jitter;
                return (
                  <div
                    key={i}
                    className={`w-1 rounded ${voiceMachine.state === "CAPTURING" ? "bg-red-400" : "bg-blue-400"}`}
                    style={{ height: `${height}px`, transition: "height 60ms linear" }}
                  />
                );
              })
            ) : (
              <span className="text-sm text-gray-700">{voiceStateLabel[voiceMachine.state]}</span>
            )}
          </div>
          <span className="text-sm text-gray-700 mr-2">{voiceStateLabel[voiceMachine.state]}</span>
        </div>
      ) : (
        // 📝 Show normal input UI
        <div className="flex items-center bg-white p-2 shadow-md rounded-[32px] chat-input sticky bottom-0 w-full">
          <input
            type="text"
            placeholder="Ask anything you need"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="flex-1 pl-[18px] p-2 outline-none border-none text-gray-700"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (!loading) sendMessage();
              }
            }}
            disabled={loading}
          />
          {/* Dynamic button based on message */}
          {message.trim() ? (
            <button
              onClick={sendMessage}
              disabled={loading}
              style={{
                opacity: loading ? 0.5 : 1,
                pointerEvents: loading ? "none" : "auto",
                cursor: loading ? "not-allowed" : "pointer",
              }}
            >
              <img src={Send} alt="Send Icon" className="send-img" />
            </button>
          ) : (
            <button onClick={handleMicPress} title="Start conversation">
              <div className="microphone-icon">
                <img src={Microphone} alt="Mic Icon" className="" />
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );
};
export default ChatInput;